'use client';

import type { MantineColorsTuple } from '@mantine/core';

/**
 * Blackroom palette of base colors
 */
export const palette = {
  blackroomWhite: '#FFFFFF',
  blackroomBlue: '#4377DC',
  blackroomGreen: '#11C775',
  blackroomOrange: '#FF9D42',
  blackroomRed: '#FE555C',
  blackroomGray: '#A9B2B9',
  blackroomBlack: '#0D0D0D',
  blackroomYellow: '#FEC864',
  blackroomViolet: '#7C3AED', // used for dataroom / AI-adjacent surfaces
  /**
   * These 4 gray colors are defined
   * without open-color shade counterparts
   */
  blackroomDarkGray1: '#2F3438', // => @TODO: Found in Figma, what for?
  blackroomDarkGray2: '#222629', // => Widget background color in dark mode + menu background
  blackroomDarkGray3: '#444444', // => @TODO: Found in Figma, what for?
  blackroomDarkGray4: '#4B4B4B', // => Text / resource menu meta first col
  blackroomDarkGray5: '#2B3135', // => Hover on blackroomDarkGray2
  blackroomDarkGray6: '#3C3C3C', // => Border color for blackroomDarkGray2 backgrounds

  blackroomLightGray1: '#F6F7F9', // => @TODO: Found in Figma, what for?
  blackroomLightGray2: '#7C7C7C', // => Text / icon color
  blackroomLightGray3: '#F3F3F3', // => Text / icon color
  blackroomLightGray4: '#FAFAFF', // => File viewer canvas
  blackroomLightGray5: '#979797', // => Text / resource menu meta second col
  blackroomLightGray6: '#848A95', // => Text / icon in search
  blackroomLightGray7: '#d4dae0', // => Divider color

  blackroomPaleGray: '#E8E8E8', // very pale gray, used for row dividers

  blackroomAI: '#4377DC',
} as const;

/**
 * Semantic surface tokens — use these instead of hardcoding hex values
 * in component styles. Each token maps to a usage context, not a raw color.
 */
export const surfaces = {
  /** App-level background (default page surface) */
  app: palette.blackroomWhite,
  /** Cards/panels lifted above the app surface */
  raised: '#FBFCFE',
  /** Recessed surfaces: table headers, code blocks, neutral chips */
  sunken: '#F3F5F8',
  /** Row hover background — slightly tinted blue */
  hover: '#F0F7FE',
  /** Selected / active row background */
  selected: '#E7F2FE',
  /** Soft accent background (AI badges, blue tints) */
  accentSoft: 'rgba(67, 119, 220, 0.08)',
} as const;

/**
 * Overlay tokens for backdrops & glass effects (top bar, modals).
 */
export const overlays = {
  scrim: 'rgba(13, 17, 23, 0.45)',
  frostBg: 'rgba(255, 255, 255, 0.78)',
  frostBlur: 'blur(20px) saturate(180%)',
} as const;

/**
 * Open-colors version of each palette color
 * Meant to be used by the mantine theme
 * Generated with: https://noeldelgado.github.io/shadowlord
 * Place palette color in 7th position using the ticker
 */
const colors = {
  blackroomNeutral: [
    '#ffffff',
    '#bbbbbb',
    '#989898',
    '#767676',
    '#535353',
    '#303030',
    '#0d0d0d',
    '#0b0b0b',
    '#090909',
    '#070707',
  ],
  blackroomWhite: [
    '#ffffff',
    '#ffffff',
    '#ffffff',
    '#ffffff',
    '#ffffff',
    '#ffffff',
    '#ffffff',
    '#ffffff',
    '#ffffff',
    '#ffffff',
  ],
  blackroomBlue: [
    '#f0f6fe',
    '#deebfb',
    '#c5ddf8',
    '#9cc8f4',
    '#6da9ed',
    '#4b8ae6',
    '#4377dc',
    '#2d5ac8',
    '#2b4aa2',
    '#274181',
  ],
  blackroomGreen: [
    '#dff7ec',
    '#bcefd8',
    '#9ae7c4',
    '#78dfb1',
    '#56d79d',
    '#33cf89',
    '#11c775',
    '#0faa64',
    '#0c8e53',
    '#0a7142',
  ],
  blackroomOrange: [
    '#fff2e5',
    '#ffe4ca',
    '#ffd5af',
    '#ffc794',
    '#ffb978',
    '#ffab5d',
    '#ff9d42',
    '#da8638',
    '#b6702f',
    '#915925',
  ],
  blackroomRed: [
    '#ffe8e9',
    '#ffcfd1',
    '#ffb7ba',
    '#fe9ea2',
    '#fe868b',
    '#fe6d73',
    '#fe555c',
    '#d9494f',
    '#b53d42',
    '#903034',
  ],

  blackroomGray: [
    '#f3f5f5',
    '#e7e9eb',
    '#dbdee1',
    '#ced3d7',
    '#c2c8cd',
    '#b5bdc3',
    '#a9b2b9',
    '#91989e',
    '#787f84',
    '#606569',
  ],
  blackroomLightGray: [
    '#fefefe',
    '#fcfdfd',
    '#fbfcfc',
    '#fafafc',
    '#f9f9fb',
    '#f7f8fa',
    '#f6f7f9',
    '#d3d4d5',
    '#b0b0b2',
    '#8c8d8e',
  ],
  blackroomBlack: [
    '#dedede',
    '#bbbbbb',
    '#989898',
    '#767676',
    '#535353',
    '#303030',
    '#0d0d0d',
    '#0b0b0b',
    '#090909',
    '#070707',
  ],
} satisfies Record<string, MantineColorsTuple>;

export default colors;
